import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Japanese mirror of the spouse-visa document checklist guide. */
const TITLE = "配偶者ビザのタイ書類チェックリスト — 認証順序と差し戻し理由";
const DESC =
  "配偶者・パートナービザに必要なタイの書類、翻訳と認証の正しい順序、そして不許可や差し戻しにつながる典型的な誤り。";
const PATH = "/blog/spouse-visa-document-checklist";
const URL = `/ja${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "配偶者ビザに必要なタイの書類は何ですか？",
    answer:
      "婚姻登録証、婚姻状況登録記録、身分証明書と住居登録（タビアンバーン）、パスポート、子がいる場合は出生証明書、必要に応じて無犯罪証明、そして同居・関係を示す資料が一般的です。最終的な一覧は提出先の案内に従ってください。",
  },
  {
    question: "書類は何語に翻訳しますか？",
    answer:
      "提出先が受理する言語です。多くは英語ですが、提出先国の公用語を求める場合もあります。訳文は印章・スタンプ・注記まで原本と完全に対応させます。",
  },
  {
    question: "認証の順序はどうなりますか？",
    answer:
      "原本または認証済み謄本の取得 → 私文書は公証人による署名認証 → 必要に応じて外務省領事局の認証 → 全文翻訳 → 提出先国の在外公館での認証またはアポスティーユ、という順です。順序の逆転は差し戻しの代表例です。",
  },
  {
    question: "書類に有効期間はありますか？",
    answer:
      "登録関係の書類は発行から1〜6か月以内を求める機関が多いです。各段階に営業日を要するため、早く取るのではなく提出期限から逆算して取得します。",
  },
  {
    question: "海外で結婚した場合、タイ側の登録は必要ですか？",
    answer:
      "用途によります。タイの機関に婚姻状況を証明する必要がある場合は該当する登録・記録が必要です。提出先が外国機関のみであれば、その機関の要件に従います。",
  },
  {
    question: "代理人に依頼できますか？",
    answer:
      "可能ですが、委任状の権限に申請・受領・認証手続き・提出のすべてを含める必要があります。範囲が狭いと手続きが途中で止まります。",
  },
  {
    question: "不許可の主な理由は何ですか？",
    answer:
      "氏名スペルの不一致、訳文の不完全さ、認証順序の誤り、有効期間切れ、関係を示す資料の不足、原本ではなくコピーの提出などです。",
  },
];

export const Route = createFileRoute("/ja/blog/spouse-visa-document-checklist")({
  component: JaSpouseVisaChecklist,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ja" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("spouse-visa-document-checklist", "ja") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("spouse-visa-document-checklist") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "配偶者ビザ タイ 書類, タイ 婚姻登録証 翻訳 認証, パートナービザ 書類 一覧, タイ 書類 大使館 認証, タイ 婚姻状況 証明",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ja"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "ホーム", url: "/ja" },
              { name: "ガイド", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("spouse-visa-document-checklist"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Visa & Immigration",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ doc: string; note: string }> = [
  { doc: "婚姻登録証", note: "登録機関発行の原本または認証済み謄本。氏名表記はパスポートと一致させます。" },
  { doc: "婚姻状況登録記録", note: "現在の婚姻状況を示す書類。直近発行を求める機関もあります。" },
  { doc: "身分証明書・住居登録", note: "住所記録が最新で、他の書類と整合していること。" },
  { doc: "パスポート", note: "提出から審査期間をカバーする有効期限であること。" },
  { doc: "出生証明書（子がいる場合）", note: "親子関係の書類は登録機関の注記まで含めて全文翻訳します。" },
  { doc: "無犯罪証明（要求時）", note: "提出先の有効期間の範囲内に発行日が入るよう調整します。" },
];

const SEQUENCE: string[] = [
  "提出先機関の最新の書類一覧と言語要件を確認する",
  "原本または認証済み謄本を取得する",
  "私文書は公証人による署名認証を受ける",
  "必要に応じて外務省領事局の認証を受ける",
  "印章・注記まで含めて全文翻訳する",
  "在外公館の認証またはアポスティーユを取得し、全書類を照合して提出する",
];

const MISTAKES: string[] = [
  "書類間で氏名スペルが一致していない",
  "印章・スタンプ・登録注記の訳し漏れ",
  "認証より先に翻訳し、認証内容が訳文に現れていない",
  "登録書類の発行が早すぎて有効期間を過ぎている",
  "同居・交流など関係を示す資料が不足している",
  "原本の代わりにコピーを提出している",
];

function JaSpouseVisaChecklist() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ja" className="text-muted-foreground hover:text-foreground">
          ホーム
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          ガイド
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="spouse-visa-document-checklist" locale="ja" />
      <p className="answer-summary speakable-summary">
        配偶者ビザが通らない原因は書類不足よりも、氏名スペルの不一致、訳文の不完全さ、認証順序の誤りです。
        提出期限から逆算し、書類一覧と言語要件を確認したうえで「原本 → 公証人 → 領事局 → 翻訳 → 在外公館」の順に進めてください。
      </p>

      <h2>1. 書類チェックリスト</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d.doc}>
            <strong>{d.doc}：</strong> {d.note}
          </li>
        ))}
      </ul>

      <h2>2. 認証の順序</h2>
      <ol>
        {SEQUENCE.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. スケジュールの立て方</h2>
      <p>
        登録関係の書類は発行から1〜6か月以内を求められることが多く、公証・領事局・在外公館の各段階にも営業日が必要です。
        提出期限から逆算して各段階の余裕を確保すれば、手続き途中で書類が失効する事態を防げます。
      </p>

      <h2>4. よくある差し戻し理由</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>5. よくある質問</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>6. 参考リンクと関連サービス</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          タイ外務省 領事局
        </a>{" "}
        ·{" "}
        <a href="https://www.bora.dopa.go.th/" target="_blank" rel="noreferrer">
          タイ内務省 地方行政登録局
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ja/marriage" className="text-primary underline">
            婚姻・家族関係書類サービス
          </Link>
        </li>
        <li>
          <Link to="/ja/visa" className="text-primary underline">
            ビザ・移民書類サービス
          </Link>
        </li>
        <li>
          <Link to="/ja/lgz" className="text-primary underline">
            文書認証・領事認証サービス
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容確認日 {PUBLISHED}。本ガイドは計画のための一般情報であり、いかなる機関の決定を示すものではありません。手続き前に提出先機関で最新要件をご確認ください。
      </p>
    </article>
  );
}
